// pages/SubmissionDetails.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchSubmissions, deleteSubmission } from '../../utils/api'; // Import deleteSubmission
import ConfirmationModal from '../ConfirmationModal'; // Import the modal

const SubmissionDetails = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for confirmation modal
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchSubmissions();
        const specificSubmission = data.find(
          (item) => item.submissionId === submissionId
        );
        if (!specificSubmission) {
          throw new Error('Submission not found.');
        }
        setSubmission(specificSubmission);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [submissionId]);

  /**
   * Handle the deletion of a submission.
   */
  const confirmDelete = async () => {
    if (!submission) return;

    try {
      await deleteSubmission(submissionId);
      // Optionally, show a success message
      alert("Submission deleted successfully.");
      navigate('/dashboard'); // Redirect back to dashboard
    } catch (err) {
      console.error("Error deleting submission:", err);
      alert(`Failed to delete submission: ${err.message}`);
    }
  };

  /**
   * Open the confirmation modal.
   */
  const handleDelete = () => {
    setIsConfirmModalOpen(true);
  };

  // Always render the container, even during loading or error states
  return (
    <div className="min-h-screen bg-gray-900 p-8 text-gray-100">
      <button
        onClick={() => navigate(-1)}
        className="mb-6 bg-gray-700 text-gray-100 px-4 py-2 rounded hover:bg-gray-600 transition duration-300"
      >
        Back
      </button>

      {loading && <p>Loading submission details...</p>}
      {error && !loading && <p className="text-red-400">Error: {error}</p>}
      {!loading && !error && !submission && <p>Submission not found.</p>}

      {submission && (
        <>
          <h1 className="text-3xl font-bold mb-6">
            Submission Details
          </h1>
          <div className="bg-gray-800 rounded-lg shadow p-6 space-y-4">
            <p>
              <strong className="text-gray-300">Name:</strong> {submission.fullName}
            </p>
            <p>
              <strong className="text-gray-300">Phone:</strong> {submission.phoneNumber}
            </p>
            <p>
              <strong className="text-gray-300">Email:</strong> {submission.email}
            </p>
            <p>
              <strong className="text-gray-300">Length of Sobriety:</strong> {submission.lengthOfSobriety}
            </p>
            <p>
              <strong className="text-gray-300">Desired Move-In Date:</strong> {submission.desiredMoveInDate}
            </p>
            <p>
              <strong className="text-gray-300">Coming from Treatment Center:</strong> {submission.treatmentCenter}
            </p>
            <p>
              <strong className="text-gray-300">Treatment Center Name:</strong> {submission.treatmentCenterName}
            </p>
            <p>
              <strong className="text-gray-300">Case Manager Name:</strong> {submission.caseManagerName}
            </p>
            <p>
              <strong className="text-gray-300">Case Manager Contact:</strong> {submission.caseManagerContact}
            </p>
            <p>
              <strong className="text-gray-300">Waiting List:</strong> {submission.waitingList}
            </p>
            <p>
              <strong className="text-gray-300">Timestamp:</strong> {new Date(
                submission.timestamp
              ).toLocaleString()}
            </p>
            {/* Delete Button */}
            <button
              onClick={handleDelete}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition duration-300"
            >
              Delete Submission
            </button>
          </div>
        </>
      )}

      {/* Confirmation Modal for Deletion */}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        title="Confirm Deletion"
        message="Are you sure you want to delete this submission? This action cannot be undone."
        onConfirm={confirmDelete}
        onCancel={() => setIsConfirmModalOpen(false)}
      />
    </div>
  );
};

export default SubmissionDetails;
